import React, { useState, useEffect } from "react";
import { Button, Table, Space, Popconfirm, message } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import { adminRestaurantPosService } from "../../../services/adminService";
import KitchenGroupFormModal, { KitchenGroup } from "./KitchenGroupFormModal";

interface KitchenGroupManagementProps {
  visible: boolean;
}

const KitchenGroupManagement: React.FC<KitchenGroupManagementProps> = ({ visible }) => {
  const [groups, setGroups] = useState<KitchenGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingGroup, setEditingGroup] = useState<KitchenGroup | null>(null);

  const fetchGroups = async () => {
    try {
      setLoading(true);
      const res = await adminRestaurantPosService.getKitchenGroups();
      setGroups(((res as any).data || []) as KitchenGroup[]);
    } catch {
      message.error("Failed to fetch kitchen groups");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (visible) fetchGroups();
  }, [visible]);

  const handleSave = async (values: any) => {
    try {
      if (editingGroup) {
        await adminRestaurantPosService.updateKitchenGroup(editingGroup.id, values);
        message.success("Kitchen group updated");
      } else {
        await adminRestaurantPosService.createKitchenGroup(values);
        message.success("Kitchen group created");
      }
      setModalVisible(false);
      setEditingGroup(null);
      fetchGroups();
    } catch (error: any) {
      message.error(error?.response?.data?.message || error?.message || "Failed to save kitchen group");
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await adminRestaurantPosService.deleteKitchenGroup(id);
      message.success("Kitchen group deleted");
      fetchGroups();
    } catch (error: any) {
      message.error(error?.response?.data?.message || error?.message || "Failed to delete kitchen group");
    }
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Display Order",
      dataIndex: "display_order",
      key: "display_order",
      width: 120,
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      render: (_: any, record: KitchenGroup) => (
        <Space>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => {
              setEditingGroup(record);
              setModalVisible(true);
            }}
          />
          <Popconfirm
            title="Delete this kitchen group?"
            onConfirm={() => handleDelete(record.id)}
          >
            <Button type="link" size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  if (!visible) return null;

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => {
            setEditingGroup(null);
            setModalVisible(true);
          }}
        >
          Add Kitchen Group
        </Button>
      </Space>
      <Table
        rowKey="id"
        columns={columns}
        dataSource={groups}
        loading={loading}
        pagination={false}
        size="small"
      />
      <KitchenGroupFormModal
        visible={modalVisible}
        editingGroup={editingGroup}
        onCancel={() => {
          setModalVisible(false);
          setEditingGroup(null);
        }}
        onOk={handleSave}
      />
    </div>
  );
};

export default KitchenGroupManagement;
